import { Avatar, Tag, Tooltip, Button, Popconfirm } from 'antd';
import { EditFilled, DeleteFilled, ReloadOutlined, CalendarOutlined, UserOutlined, EyeOutlined, FolderOutlined, CopyOutlined } from '@ant-design/icons';
import Moment from 'react-moment';
import { NavLink } from 'react-router-dom';
import { useEffect } from 'react';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { directoriesState } from '../../state/actions/directoriesState';
import { tachesState } from '../../state/actions/tachesState';
import { TacheService } from '../../services/TacheService';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { isTacheLocked } from '../../helpers/permissions';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import AiSummarySection from '../ai/AiSummarySection';
import styles from './TacheHero.module.scss';
import PropTypes from 'prop-types';

export default function TacheHero({ tache, loading, onEdit, onDelete, onRefresh, onFollow, canManage }) {
  const personnels = useSelector(() => state$.personnelsDirectory.list.get());
  const canEdit = canManage && !isTacheLocked(tache);

  useEffect(() => { directoriesState.loadPersonnels(); }, []);

  const handleFieldChange = (value, fieldName, record) => {
    TacheService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => tachesState.updateTacheInList(r.data))
      .catch(catchError);
  };

  const handleCopyLink = () => copyLinkToClipboard(`/tache/${tache.uid}`, 'Lien de la tâche');

  return (
    <div className={styles.hero}>
      <div className={styles.heroBand} style={{ background: tache.priorite?.couleur ?? '#3E65AC' }} />
      <div className={styles.heroContent}>
        <div className={styles.heroLeft}>
          <div className={styles.heroMeta}>
            <Tooltip title="Copier le lien de la tâche">
              <span className={styles.heroCode} onClick={handleCopyLink} style={{ cursor: 'pointer' }}>
                {tache.code} <CopyOutlined style={{ fontSize: 11 }} />
              </span>
            </Tooltip>
            {tache.statut?.nom && (
              <Tag color={tache.statut?.couleur ?? 'default'} className={styles.heroTag}>{tache.statut.nom}</Tag>
            )}
            {tache.priorite?.nom && (
              <Tag color={tache.priorite?.couleur ?? 'default'} className={styles.heroTag}>{tache.priorite.nom}</Tag>
            )}
            {tache.retard && !tache.statut?.resolu && (
              <Tag color="red" className={styles.heroTag}>En retard</Tag>
            )}
          </div>
          <h1 className={styles.heroTitle}>
            <InlineEditableInput
              canEdit={canEdit}
              type="text"
              fieldName="nom"
              value={tache.nom}
              record={tache}
              onChange={handleFieldChange}
            >
              {tache.nom ?? '—'}
            </InlineEditableInput>
          </h1>
          <div className={styles.heroInfo}>
            <span className={styles.heroInfoItem}>
              <UserOutlined className={styles.heroInfoIcon} />
              <InlineEditableInput
                canEdit={canEdit}
                type="select"
                fieldName="responsable_id"
                value={tache.responsable_id}
                record={tache}
                onChange={handleFieldChange}
                options={personnels.map(p => ({
                  value: p.id,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar size={20} src={p.avatar_link}>{p.name?.[0]}</Avatar>
                      {p.name}
                    </span>
                  ),
                  children: p.name,
                }))}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Avatar size={20} src={tache.responsable?.avatar_link}>{tache.responsable?.name?.[0]}</Avatar>
                  <span>{tache.responsable?.name ?? 'Sans responsable'}</span>
                </span>
              </InlineEditableInput>
            </span>
            {tache.date_limite && (
              <span className={styles.heroInfoItem}>
                <CalendarOutlined className={styles.heroInfoIcon} />
                <Moment format="DD MMM YYYY">{tache.date_limite}</Moment>
              </span>
            )}
            {tache.affaire && (
              <span className={styles.heroInfoItem}>
                <FolderOutlined className={styles.heroInfoIcon} />
                <NavLink to={`/affaire/${tache.affaire.uid}`}>{tache.affaire.nom}</NavLink>
              </span>
            )}
          </div>
        </div>
        <div className={styles.heroActions}>
          <Tooltip title="Rafraîchir">
            <Button type="text" icon={<ReloadOutlined />} loading={loading} onClick={onRefresh} />
          </Tooltip>
          {!tache.current_member && canManage && (
            <Tooltip title="Suivre la tâche pour recevoir les notifications.">
              <Button type="default" icon={<EyeOutlined />} onClick={onFollow}>Suivre</Button>
            </Tooltip>
          )}
          {canEdit && (
            <Button type="primary" icon={<EditFilled />} onClick={onEdit}>Éditer</Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Supprimer cette tâche ?"
              onConfirm={onDelete}
              okText="Supprimer"
              okButtonProps={{ danger: true }}
              cancelText="Annuler"
            >
              <Button danger icon={<DeleteFilled />}>Supprimer</Button>
            </Popconfirm>
          )}
        </div>
      </div>
      <div className={styles.heroSummary}>
        <AiSummarySection type="tache" uid={tache.uid} />
      </div>
    </div>
  );
}

TacheHero.propTypes = {
  tache: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onRefresh: PropTypes.func,
  onFollow: PropTypes.func,
  canManage: PropTypes.bool,
};
